/**
 * Canonical deal-type definitions shared across form, API, cards, filters, and templates.
 * Aligns with mobile dealTypes.ts plus website "other".
 */

export type DealType =
  | 'cash'
  | 'assignment'
  | 'double_close'
  | 'subject_to'
  | 'seller_finance'
  | 'lease_option'
  | 'wraparound_mortgage'
  | 'hybrid_creative'
  | 'fsbo'
  | 'other';

export type DealTypeCategory = 'traditional' | 'creative' | 'other';

export type DealTypeDefinition = {
  value: DealType;
  label: string;
  category: DealTypeCategory;
  shortHelp: string;
};

export const DEAL_TYPES: readonly DealTypeDefinition[] = [
  {
    value: 'cash',
    label: 'Cash Deal',
    category: 'traditional',
    shortHelp: 'Buyer pays cash or conventional financing at closing.',
  },
  {
    value: 'assignment',
    label: 'Assignment',
    category: 'traditional',
    shortHelp: 'Contract rights are assigned to an end buyer for a fee.',
  },
  {
    value: 'double_close',
    label: 'Double Close',
    category: 'traditional',
    shortHelp: 'Two closings: wholesaler buys then resells.',
  },
  {
    value: 'subject_to',
    label: 'Subject-To',
    category: 'creative',
    shortHelp: 'Buyer takes ownership while the existing loan remains in place.',
  },
  {
    value: 'seller_finance',
    label: 'Seller Finance',
    category: 'creative',
    shortHelp: 'The seller receives payments over time instead of the full price at closing.',
  },
  {
    value: 'lease_option',
    label: 'Lease Option',
    category: 'creative',
    shortHelp: 'Tenant leases with an option to purchase later.',
  },
  {
    value: 'wraparound_mortgage',
    label: 'Wraparound Mortgage',
    category: 'creative',
    shortHelp: 'New buyer note wraps around an existing underlying loan.',
  },
  {
    value: 'hybrid_creative',
    label: 'Hybrid / Other Creative',
    category: 'creative',
    shortHelp: 'A blended or custom creative structure.',
  },
  {
    value: 'fsbo',
    label: 'FSBO',
    category: 'other',
    shortHelp: 'For-sale-by-owner style offering.',
  },
  {
    value: 'other',
    label: 'Other',
    category: 'other',
    shortHelp: 'Structure that does not fit the listed categories.',
  },
] as const;

export const CREATIVE_DEAL_TYPE_VALUES: readonly DealType[] = [
  'subject_to',
  'seller_finance',
  'lease_option',
  'wraparound_mortgage',
  'hybrid_creative',
];

const DEAL_TYPE_VALUES = new Set<string>(DEAL_TYPES.map((d) => d.value));
const CREATIVE_SET = new Set<string>(CREATIVE_DEAL_TYPE_VALUES);
const LABEL_BY_VALUE = new Map(DEAL_TYPES.map((d) => [d.value, d.label]));

const ALIASES: Record<string, DealType> = {
  cash: 'cash',
  'cash deal': 'cash',
  assignment: 'assignment',
  'double close': 'double_close',
  double_close: 'double_close',
  'double-close': 'double_close',
  'subject-to': 'subject_to',
  'subject to': 'subject_to',
  subject_to: 'subject_to',
  subto: 'subject_to',
  'seller finance': 'seller_finance',
  'seller financing': 'seller_finance',
  seller_finance: 'seller_finance',
  seller_financing: 'seller_finance',
  'lease option': 'lease_option',
  lease_option: 'lease_option',
  wrap: 'wraparound_mortgage',
  wraparound: 'wraparound_mortgage',
  wraparound_mortgage: 'wraparound_mortgage',
  'wraparound mortgage': 'wraparound_mortgage',
  hybrid: 'hybrid_creative',
  hybrid_creative: 'hybrid_creative',
  'hybrid creative': 'hybrid_creative',
  creative: 'hybrid_creative',
  fsbo: 'fsbo',
  other: 'other',
};

export type DealTypeFilterKey =
  | 'all'
  | 'creative'
  | DealType;

export const DEAL_TYPE_FILTER_OPTIONS: { key: DealTypeFilterKey; label: string }[] = [
  { key: 'all', label: 'All Deal Types' },
  { key: 'creative', label: 'Creative Financing' },
  { key: 'subject_to', label: 'Subject-To' },
  { key: 'seller_finance', label: 'Seller Finance' },
  { key: 'lease_option', label: 'Lease Option' },
  { key: 'wraparound_mortgage', label: 'Wraparound Mortgage' },
  { key: 'hybrid_creative', label: 'Hybrid / Other Creative' },
  { key: 'cash', label: 'Cash Deal' },
  { key: 'assignment', label: 'Assignment' },
  { key: 'double_close', label: 'Double Close' },
  { key: 'fsbo', label: 'FSBO' },
  { key: 'other', label: 'Other' },
];

export function normalizeDealType(value: unknown): DealType | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  const underscored = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (DEAL_TYPE_VALUES.has(underscored)) return underscored as DealType;
  if (ALIASES[trimmed]) return ALIASES[trimmed];
  if (ALIASES[underscored]) return ALIASES[underscored];
  return null;
}

export function getDealTypeLabel(value: unknown): string | null {
  const n = normalizeDealType(value);
  return n ? LABEL_BY_VALUE.get(n) ?? null : null;
}

export function getDealTypeDefinition(value: unknown): DealTypeDefinition | null {
  const n = normalizeDealType(value);
  return n ? DEAL_TYPES.find((d) => d.value === n) ?? null : null;
}

export function isCreativeDealType(value: unknown): boolean {
  const n = normalizeDealType(value);
  return n != null && CREATIVE_SET.has(n);
}

export function getDealTypeCategoryLabel(value: unknown): string | null {
  const def = getDealTypeDefinition(value);
  if (!def) return null;
  if (def.category === 'creative') return 'Creative Financing';
  if (def.category === 'traditional') return 'Traditional';
  return 'Other';
}

export function dealTypesForFilterKey(key: DealTypeFilterKey): string[] {
  if (key === 'all') return [];
  if (key === 'creative') return [...CREATIVE_DEAL_TYPE_VALUES];
  return [key];
}

export function dealTypeFilterKeyFromDealTypes(dealTypes: string[]): DealTypeFilterKey {
  if (!dealTypes.length) return 'all';
  const normalized = dealTypes
    .map(normalizeDealType)
    .filter((v): v is DealType => v != null);
  if (!normalized.length) return 'all';
  if (
    normalized.length === CREATIVE_DEAL_TYPE_VALUES.length &&
    CREATIVE_DEAL_TYPE_VALUES.every((c) => normalized.includes(c))
  ) {
    return 'creative';
  }
  if (normalized.length === 1) return normalized[0];
  return 'all';
}

/** Card badges: deal type when set, otherwise legacy Off-Market presentation. */
export function getListingCardBannerLabels(dealType: unknown): string[] {
  const label = getDealTypeLabel(dealType);
  if (label) return [label];
  return ['Off-Market', 'Direct from Wholesaler'];
}

export function requiresFinancingTerms(dealType: DealType | null): boolean {
  if (!dealType) return false;
  return (
    isCreativeDealType(dealType) ||
    dealType === 'assignment' ||
    dealType === 'other'
  );
}
